import { CommonModule } from '@angular/common';
import type { ComponentRef, OnDestroy } from '@angular/core';
import { Component, effect, inject, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { OfflineModeSheetComponent } from '@hunting-nature/Secacam/shared/components/offline-mode-sheet/offline-mode-sheet.component';
import { GeneralSelectors } from '@hunting-nature/Secacam/shared/redux/general/general.reducer';
import { FabMenuOptionId, TabType } from '@hunting-nature/shared-core/enums/tab-bar.enum';
import type { FabMenuOption } from '@hunting-nature/shared-core/interfaces/tab-bar.interface';
import { ComingSoonModal } from '@hunting-nature/shared-ui/components/coming-soon-modal/coming-soon-modal.component';
import { pickIcons } from '@hunting-nature/shared-ui/helper/icon-registry.const';
import type { ViewWillEnter } from '@ionic/angular';
import { IonIcon, IonTabBar, IonTabButton, IonTabs } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { addIcons } from 'ionicons';

import { OfflineBannerComponent } from '../../shared/components/offline-banner/offline-banner.component';
import { TabBarFabComponent } from '../../shared/components/tab-bar-menu/tab-bar-fab/tab-bar-fab.component';
import { TabBarMenuOverlayComponent } from '../../shared/components/tab-bar-menu/tab-bar-menu-overlay/tab-bar-menu-overlay.component';
import { TabBarVisibilityService } from '../../shared/components/tab-bar-menu/tab-bar-visibility.service';
import { GeneralActions } from '../../shared/redux/general/general.actions';
import { TabBarActions } from '../../shared/redux/tab-bar/tab-bar.actions';
import { TabBarSelectors } from '../../shared/redux/tab-bar/tab-bar.reducer';
import { AddCameraFlowActions } from '../add-camera-flow/redux/add-camera-flow.actions';
import { MenuComponent } from '../menu/menu.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
  standalone: true,
  imports: [
    TranslocoPipe,
    IonTabBar,
    IonIcon,
    IonTabButton,
    IonTabs,
    CommonModule,
    FormsModule,
    MenuComponent,
    TabBarFabComponent,
    TabBarMenuOverlayComponent,
    OfflineBannerComponent,
    OfflineModeSheetComponent,
    ComingSoonModal,
  ],
})
export class HomePage implements ViewWillEnter, OnDestroy {
  @ViewChild('bleAnchor', { read: ViewContainerRef })
  private readonly bleAnchor!: ViewContainerRef;

  private bleComponentRef: ComponentRef<unknown> | null = null;

  // services
  private readonly tabBarVisibility = inject(TabBarVisibilityService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  // UI bindings
  protected readonly isBLEFeatureEnabled: boolean = FEATURE_BLE;
  protected readonly tabBarShown$$ = this.tabBarVisibility.tabBarShown$$;
  protected readonly tabBarMenuOpen$$ = this.store.selectSignal(TabBarSelectors.tabBarMenuOpen);
  protected readonly menuConfig$$ = this.store.selectSignal(TabBarSelectors.menuConfig);
  protected readonly expandedOptionId$$ = this.store.selectSignal(TabBarSelectors.expandedOptionId);
  private readonly isOfflineMode$$ = this.store.selectSignal(GeneralSelectors.isOfflineMode);
  protected readonly isOfflineModalVisible$$ = this.store.selectSignal(GeneralSelectors.isOfflineModalVisible);
  protected readonly isComingSoonModalVisible$$ = this.store.selectSignal(GeneralSelectors.isComingSoonModalVisible);

  constructor() {
    addIcons(pickIcons('tab-cameras', 'tab-gallery'));
    effect(() => {
      const isMenuOpen = this.tabBarMenuOpen$$();
      const expandedId = this.expandedOptionId$$();
      const shouldShow = isMenuOpen && expandedId === FabMenuOptionId.BleDevice && FEATURE_BLE;

      if (shouldShow) {
        void this.loadBleFlowComponent();
      } else if (this.bleComponentRef) {
        this.bleComponentRef.destroy();
        this.bleComponentRef = null;
      }
    });
  }

  ngOnDestroy(): void {
    this.bleComponentRef?.destroy();
  }

  ionViewWillEnter(): void {
    const tab = this.getTabFromUrl(this.router.url);
    if (tab) {
      this.store.dispatch(TabBarActions.selectTab({ tab }));
    }
  }

  private getTabFromUrl(url: string): TabType | undefined {
    if (url.includes('/gallery')) return TabType.Gallery;
    if (url.includes('/cameras')) return TabType.Cameras;
    /* Ballistics & Map pages to be restored in future release 
    if (url.includes('/ballistics')) return TabType.Ballistics;
    if (url.includes('/map')) return TabType.Map; */
    return undefined;
  }

  handleGotoGallery(): void {
    this.store.dispatch(TabBarActions.selectTab({ tab: TabType.Gallery }));
  }

  handleGotoCameras(): void {
    this.store.dispatch(TabBarActions.selectTab({ tab: TabType.Cameras }));
  }

  /* Ballistics & Map pages to be restored in future release 
  handleGotoBallistics(): void {
    this.store.dispatch(TabBarActions.selectTab({ tab: TabType.Ballistics }));
  }
  handleGotoMap(): void {
    this.store.dispatch(TabBarActions.selectTab({ tab: TabType.Map }));
  }*/

  handleFabLabelClick(): void {
    if (this.isOfflineMode$$()) {
      this.store.dispatch(GeneralActions.openOfflineModal());
      return;
    }

    this.store.dispatch(TabBarActions.toggleTabBarMenu());
  }

  handleBackdropClick(): void {
    this.store.dispatch(TabBarActions.closeTabBarMenu());
  }

  handleMenuOptionClick(option: FabMenuOption): void {
    switch (option.id) {
      case FabMenuOptionId.Secacam:
        this.store.dispatch(TabBarActions.closeTabBarMenu());
        this.store.dispatch(AddCameraFlowActions.openAddCameraFlow());
        break;
      case FabMenuOptionId.BleDevice:
        if (FEATURE_BLE) {
          this.store.dispatch(TabBarActions.expandOption({ optionId: FabMenuOptionId.BleDevice }));
        }
        break;
      case FabMenuOptionId.CreateAlbum:
        this.store.dispatch(TabBarActions.closeTabBarMenu());
        // TODO: Implement album creation
        break;
    }
  }

  handleExpandedOptionCollapse(): void {
    this.store.dispatch(TabBarActions.collapseOption());
  }

  handleSideMenuOpen(): void {
    this.store.dispatch(GeneralActions.menuOpened());
  }

  private async loadBleFlowComponent(): Promise<void> {
    if (this.bleComponentRef || !this.bleAnchor) return;

    const { AddBleDeviceFlowComponent } = await import('../../shared/components/add-ble-device-flow/add-ble-device-flow.component');

    this.bleAnchor.clear();
    const reference = this.bleAnchor.createComponent(AddBleDeviceFlowComponent);
    this.bleComponentRef = reference;

    reference.instance.addDeviceFlowClose.subscribe(() => {
      this.handleExpandedOptionCollapse();
    });
  }

  protected handleOfflineModalClose(): void {
    this.store.dispatch(GeneralActions.closeOfflineModal());
  }

  protected handleUpdateStatus(): void {
    this.store.dispatch(GeneralActions.updateOfflineStatus());
  }

  protected handleGoToSystemSettings(): void {
    this.store.dispatch(GeneralActions.goToSystemSetting());
  }

  protected handleComingSoonModalClose(): void {
    this.store.dispatch(GeneralActions.closeComingSoonModal());
  }
}
